/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

/**
 * Pulsante FAQ di sezione: piccolo, in testata a destra di ogni pagina.
 *
 * Perché esiste: le FAQ stavano solo in Supporto, e per una domanda nata
 * mentre si lavora — "perché non posso passare direttamente a BATTUTA?" —
 * l'operatore doveva lasciare la pagina, aprire Supporto e cercare dentro
 * l'elenco intero. In campo, a quel punto, si rinuncia e si chiede al collega.
 * Qui vede SOLO le domande della schermata in cui si trova.
 *
 * Il filtro è per **singola voce** (campo `pagine` in faqData.js), non per
 * categoria: le categorie sono tematiche e una sola contiene voci di cinque
 * schermate diverse.
 *
 * Discreto per scelta: icona sola, stile secondario, nessun contatore. Non deve
 * competere con le azioni della pagina, che sono il lavoro vero.
 */
import { useState } from "react";
import { HelpCircle, ChevronDown, X, Search } from "lucide-react";
import { faqPerPagina, PAGINE_FAQ } from "./faqData";

/** Voce a fisarmonica. Condivisa con l'elenco completo in SupportoPage. */
export const FaqItem = ({ voce, aperta, onToggle }) => (
  <div className="tactical-card p-0 overflow-hidden">
    <button
      className="w-full flex items-start gap-3 p-4 text-left hover:bg-slate-800/70 transition-colors"
      onClick={onToggle}
      aria-expanded={aperta}
    >
      <HelpCircle size={16} className="text-orange-400 flex-shrink-0 mt-0.5" />
      <span className="flex-1 text-white text-sm font-medium">{voce.q}</span>
      <ChevronDown
        size={16}
        className={`text-slate-500 flex-shrink-0 mt-0.5 transition-transform ${aperta ? "rotate-180" : ""}`}
      />
    </button>
    {aperta && (
      <div className="px-4 pb-4 pl-[43px] text-sm text-slate-300 leading-relaxed whitespace-pre-line">
        {voce.a}
      </div>
    )}
  </div>
);

/** Pannello con le sole FAQ della schermata corrente. */
const FaqPaginaModal = ({ pagina, voci, onClose, onVaiASupporto }) => {
  const [aperta, setAperta] = useState(voci.length === 1 ? voci[0].id : null);
  const nome = PAGINE_FAQ[pagina] || "questa schermata";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      {/* `p-5`: `.tactical-card` definisce il padding solo sotto i 768px */}
      <div className="tactical-card w-full max-w-lg max-h-[85vh] flex flex-col p-5 sm:p-6">
        <div className="flex items-start justify-between mb-4 flex-shrink-0">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2">
              <HelpCircle size={16} className="text-orange-400" /> Domande su {nome}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {voci.length} {voci.length === 1 ? "domanda" : "domande"} su questa schermata
            </p>
          </div>
          <button className="text-slate-400 hover:text-white" onClick={onClose} aria-label="Chiudi">
            <X size={18} />
          </button>
        </div>

        {/* `pr-2 -mr-2`: la barra di scroll sta fuori dal testo, non sopra */}
        <div className="flex-1 overflow-y-auto min-h-0 pr-2 -mr-2 space-y-2">
          {voci.map((v) => (
            <FaqItem
              key={v.id}
              voce={v}
              aperta={aperta === v.id}
              onToggle={() => setAperta(aperta === v.id ? null : v.id)}
            />
          ))}
        </div>

        {/* Via d'uscita verso l'elenco intero: qui c'è solo la fetta di questa
            schermata, e la domanda potrebbe essere di un'altra. */}
        <div className="flex flex-wrap gap-2 justify-between items-center border-t border-slate-700 pt-4 mt-4 flex-shrink-0">
          {onVaiASupporto ? (
            <button
              className="btn btn-secondary btn-sm flex items-center gap-2"
              onClick={() => { onClose(); onVaiASupporto(); }}
              title="Apri la pagina Supporto con tutte le FAQ e la richiesta di assistenza"
            >
              <Search size={14} /> Cerca in tutte le FAQ
            </button>
          ) : <span />}
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Il pulsante. Si mette come PRIMO elemento del gruppo azioni in testata, così
 * sta a destra ma prima dei pulsanti operativi, che restano i più a destra e
 * quindi i più raggiungibili.
 *
 * `pagina`      — la chiave di PAGINE_FAQ (di norma `currentPage` di App.js)
 * `onNavigate`  — facoltativo: senza, sparisce solo il rimando a Supporto
 */
const FaqPagina = ({ pagina, onNavigate }) => {
  const [aperto, setAperto] = useState(false);
  const voci = faqPerPagina(pagina);

  // Niente pulsante dove non c'è niente da mostrare: un bottone che apre un
  // pannello vuoto insegna all'operatore a ignorarlo.
  if (!voci.length) return null;

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => setAperto(true)}
        title={`Domande frequenti su ${PAGINE_FAQ[pagina] || "questa schermata"}`}
        aria-label="Domande frequenti su questa schermata"
        data-testid="btn-faq-pagina"
      >
        <HelpCircle size={15} />
      </button>

      {aperto && (
        <FaqPaginaModal
          pagina={pagina}
          voci={voci}
          onClose={() => setAperto(false)}
          onVaiASupporto={onNavigate ? () => onNavigate("supporto") : undefined}
        />
      )}
    </>
  );
};

export default FaqPagina;
